import storageManager from './storageManager';

interface NotificationAdminConfig {
  userId: string;
  accessDate: number;
  noShowNotificationListAdminId: number[];
}

const localStorageNotificationAdminKey = 'NOTIFICATION_ADMIN_CONFIG';

/**
 * Główna funkcja filtrująca przekazaną tablicę notyfikacji po następujących wytycznych:
 * (modificationDate po ostatnim accessDate lub accessDate nie dzisiaj) i (pole always display na true lub id notyfikacji nie znajduje się na liście do ignorowania)
 *
 * @param notificationListAdmin
 */
export const filterNotificationListAdmin = (
  notificationListAdmin: AppConfig.NotificationAdmin[],
  userId: string,
): AppConfig.NotificationAdmin[] => {
  // odczytanie konfiguracji notyfikacji z localStorage
  const notificationAdminConfigUser = getNotificationAdminConfigUserFromStorage(userId);

  // jeśli nie ma żadnej konfiguracji to zwróć całą listę powiadomień
  if (!notificationAdminConfigUser) {
    setAccessDate(userId); // ustawienie daty ostatniej próby odczytania notyfikacji przez usera
    return notificationListAdmin;
  }

  // funkcja do usuwania zbędnych id lub zmodyfikowanych
  const modifiedNotificationAdminConfigUser = deleteRedundantIdsFromStorage(notificationAdminConfigUser, notificationListAdmin);

  // czy dostęp był dzisiaj
  const isAccessWasToday = checkAccessDateIsToday(modifiedNotificationAdminConfigUser.accessDate);

  // przefiltruj dane zgodnie z wytycznymi: (modificationDate po ostatnim accessDate lub accessDate nie dzisiaj) i (pole always display na true lub id notyfikacji nie znajduje się na liście do ignorowania)
  const filteredData = notificationListAdmin.filter(
    notificationAdmin =>
      (notificationAdmin.modificationDate >= new Date(modifiedNotificationAdminConfigUser.accessDate) || !isAccessWasToday) &&
      (notificationAdmin.alwaysDisplay ||
        !modifiedNotificationAdminConfigUser.noShowNotificationListAdminId.includes(notificationAdmin.id)),
  );

  setAccessDate(userId); // ustawienie daty ostatniej próby odczytania notyfikacji przez usera
  return filteredData;
};

/**
 * Pobranie konfiguracji notyfikacji dla wszystkich użytkowników z local storage
 *
 */
const getNotificationAdminConfigListFromStorage = (): NotificationAdminConfig[] | null => {
  const notificationAdminConfigStorage = storageManager.get(localStorageNotificationAdminKey);

  let notificationAdminConfigList: NotificationAdminConfig[] | null = null;

  if (notificationAdminConfigStorage) {
    try {
      notificationAdminConfigList = JSON.parse(notificationAdminConfigStorage);
    } catch {
      storageManager.remove(localStorageNotificationAdminKey);
    }
  }

  return notificationAdminConfigList;
};

/**
 * Pobranie konfiguracji notyfikacji dla wskazanego użytkownika z local storage
 *
 * @param userId
 */
const getNotificationAdminConfigUserFromStorage = (userId: string): NotificationAdminConfig | undefined => {
  const notificationAdminConfigList = getNotificationAdminConfigListFromStorage();
  const notificationAdminConfig = notificationAdminConfigList?.find(config => config.userId === userId);
  const validatedNotificationAdminConfig = validateDataFromLocalStorage(notificationAdminConfig);

  return validatedNotificationAdminConfig;
};

/**
 * Wstawianie konfiguracji do local storage
 *
 */
const setNotificationAdminConfigToStorage = (data: NotificationAdminConfig): void => {
  const notificationAdminConfigStorage = getNotificationAdminConfigListFromStorage();

  // jeśli w konfiguracji jest już podany user to podmień konfiguracje
  if (notificationAdminConfigStorage?.find(config => config.userId === data.userId)) {
    const newStorageData = notificationAdminConfigStorage.map(config => (config.userId === data.userId ? data : config));
    storageManager.set(localStorageNotificationAdminKey, `${JSON.stringify(newStorageData)}`);
  } else if (notificationAdminConfigStorage) {
    // jeśli nie ma to dodaj jego konfuracje
    const newStorageData = [...notificationAdminConfigStorage, data];
    storageManager.set(localStorageNotificationAdminKey, `${JSON.stringify(newStorageData)}`);
  } else {
    // jeśli nie ma kofiguracji to stwórz ją
    storageManager.set(localStorageNotificationAdminKey, `${JSON.stringify([data])}`);
  }
};

/**
 * Dodawanie id notyfikacji do listy, aby nie zobaczyć jej w przyszłości
 *
 * @param notificationAdminId
 */
export const addIdToNotificationAdminConfig = (notificationAdminId: number, userId: string): void => {
  const notificationAdminConfig = getNotificationAdminConfigUserFromStorage(userId);

  const notificationIdList = notificationAdminConfig
    ? [...notificationAdminConfig.noShowNotificationListAdminId, notificationAdminId]
    : [notificationAdminId];

  const changedNotificationAdminConfig = {
    userId,
    accessDate: Date.now(),
    noShowNotificationListAdminId: notificationIdList,
  };

  setNotificationAdminConfigToStorage(changedNotificationAdminConfig);
};

/**
 * Usuwanie id notyfikacji z listy, aby jednak ją zobaczyć w przyszłości
 *
 * @param notificationAdminId
 */
export const removeIdFromNotificationAdminConfig = (notificationAdminId: number, userId: string): void => {
  const notificationAdminConfig = getNotificationAdminConfigUserFromStorage(userId);

  const notificationIdList = notificationAdminConfig
    ? notificationAdminConfig.noShowNotificationListAdminId.filter(notificationId => notificationId !== notificationAdminId)
    : [];

  const changedNotificationAdminConfig = { userId, accessDate: Date.now(), noShowNotificationListAdminId: notificationIdList };

  setNotificationAdminConfigToStorage(changedNotificationAdminConfig);
};

/**
 * Ustawienie daty ostatniego dostępu do notyfikacji
 *
 */
const setAccessDate = (userId: string): void => {
  const notificationAdminConfig = getNotificationAdminConfigUserFromStorage(userId);

  const changedNotificationAdminConfig = {
    userId,
    accessDate: Date.now(),
    noShowNotificationListAdminId: notificationAdminConfig ? notificationAdminConfig.noShowNotificationListAdminId : [],
  };

  setNotificationAdminConfigToStorage(changedNotificationAdminConfig);
};

/**
 * Funkcja sprawdzająca czy data ostatniego dostępu była dzisiaj
 *
 * @param accessDate
 */
const checkAccessDateIsToday = (accessDate: number): boolean => {
  const today = new Date().setHours(0, 0, 0, 0);
  const access = new Date(accessDate).setHours(0, 0, 0, 0);

  return today === access;
};

/**
 * Usuwanie zbędnych lub zmodyfikowanych id
 *
 */
const deleteRedundantIdsFromStorage = (
  dataFromStorage: NotificationAdminConfig,
  notificationListAdmin: AppConfig.NotificationAdmin[],
): NotificationAdminConfig => {
  const changedNotificationId = dataFromStorage.noShowNotificationListAdminId.filter(id => {
    const notification = notificationListAdmin.find(notification => notification.id === id);

    if (!notification || (notification && notification.modificationDate > new Date(dataFromStorage.accessDate))) {
      removeIdFromNotificationAdminConfig(id, dataFromStorage.userId);
      return false;
    }

    return true;
  });

  return {
    ...dataFromStorage,
    noShowNotificationListAdminId: changedNotificationId,
  };
};

/**
 * Funkcja sprawdzająca czy dane z local storage są oraz czy są poprawne
 *
 * @param notificationAdminConfig
 */
const validateDataFromLocalStorage = (
  notificationAdminConfig: NotificationAdminConfig | undefined,
): NotificationAdminConfig | undefined => {
  if (
    notificationAdminConfig &&
    (typeof notificationAdminConfig.userId !== 'string' ||
      !notificationAdminConfig.accessDate ||
      typeof notificationAdminConfig.accessDate !== 'number' ||
      !notificationAdminConfig.noShowNotificationListAdminId ||
      !Array.isArray(notificationAdminConfig.noShowNotificationListAdminId))
  ) {
    storageManager.remove(localStorageNotificationAdminKey);
    return undefined;
  }

  return notificationAdminConfig;
};
